import { Controller, useForm, type SubmitHandler } from "react-hook-form"
import Typography from '@mui/material/Typography'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'

import useFormLocalStorage from "@/hooks/use-form-local-storage"
import { APP_VERSION, ROUTE_SEGMENTS, YEARS_FROM_START_TO_NEXT } from "@/constants/general"
import Form from "@/components/forms/reusables/form"
import useFormStates from "@/hooks/use-form-states"
import type { MotivationModelDuplicateFormInput, MotivationModelListItem } from "@/types"
import { useDuplicateMotivationModel } from "@/hooks/api/use-motivation-model"
import { useNavigate } from "react-router-dom"
import RHFTextfield from "@/components/form-fields/rhf-textfield"

const MotivationModelDuplicateForm = ({
    motivationModel,
    closeCallback,
} : {
    motivationModel: MotivationModelListItem,
    closeCallback?: () => void,
}) => {

    const navigate = useNavigate()
    
    const formDataId = `motivationModelDuplicateForm-${motivationModel?.id}-v${APP_VERSION}`

    const initialValues: MotivationModelDuplicateFormInput = {
        name: motivationModel?.name != null ? motivationModel.name+' kopie' : '',
        year: motivationModel?.year != null ? motivationModel.year.toString() : YEARS_FROM_START_TO_NEXT[0].toString(),
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const [isLoadedFromLocalStorage, handleFormReset] = useFormLocalStorage({
        control,
        formDataId,
        reset,
        initialValues
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
    } = useFormStates(handleFormReset)

    const mutation = useDuplicateMotivationModel(
        motivationModel.id,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback(newMotivationModel) {
                navigate(`/${ROUTE_SEGMENTS.base}/${ROUTE_SEGMENTS.municipalityManagement}/${ROUTE_SEGMENTS.motivationProgram}/${newMotivationModel.id}/${ROUTE_SEGMENTS.step}/0`)
            },
        },
    )

    const onSubmit: SubmitHandler<MotivationModelDuplicateFormInput> = data => {
        setRequestInProgress(true)
        mutation.mutate(data)
    }

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={isLoadedFromLocalStorage}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            buttonText="Duplikovat"
        >
            <Typography variant="h5" component="h3" gutterBottom>
                Zadejte nový název a rok
            </Typography>
            <RHFTextfield
                name="name"
                control={control}
                label="Název"
                required
            />
            <FormControl fullWidth>
                <InputLabel id="year-select-label">Rok</InputLabel>
                <Controller
                    name="year"
                    control={control}
                    render={({ field }) => (
                        <Select {...field} label="Rok">
                            {YEARS_FROM_START_TO_NEXT.map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
        </Form>
    )
}

export default MotivationModelDuplicateForm
